import React from 'react';
import { useIntl } from 'react-intl';
import { useWindowDimensions } from 'react-native';

import { getContentfulImageUrl } from '@garnish/media';

import { GiftCardsGrid } from '../../components';
import { giftCardsScreenCategoriesGridMessages as messages } from './GiftCardsScreenCategoriesGrid.messages';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardsScreenCategoriesGrid = (props: GiftCardsScreenHeaderProps) => {
  const { categories, isSignedIn, onSignedOutCardPress } = props;

  const { scale } = useWindowDimensions();

  const { formatMessage } = useIntl();

  const shouldJustifyCenter =
    categories.flatMap((category) => category.fields.giftCards).length < 3;

  return (
    <GiftCardsGrid.Container shouldJustifyCenter={shouldJustifyCenter}>
      {categories.map((category) => {
        const { giftCards } = category.fields;

        return giftCards.map((giftCard) => {
          const { id, title, cover, coverStatic } = giftCard.fields;

          const coverSrc = getImageUrl(cover, scale);
          const coverStaticSrc = coverStatic ? getImageUrl(coverStatic, scale) : undefined; // prettier-ignore

          if (!coverSrc) return null;

          return (
            <GiftCardsGrid.Item
              key={id}
              id={id}
              coverSrc={coverSrc}
              coverStaticSrc={coverStaticSrc}
              accessibilityLabel={formatMessage(messages.gridItemA11yLabel, {
                gift_card: title,
              })}
              isSignedIn={isSignedIn}
              onSignedOutCardPress={onSignedOutCardPress}
              imgAccessibilityLabel={title}
            />
          );
        });
      })}
    </GiftCardsGrid.Container>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getImageUrl(asset: Asset, scale: number) {
  return getContentfulImageUrl(asset.fields.file.url, {
    w: scale === 1 ? 400 : 800,
  });
}

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardsScreenHeaderProps = {
  categories: {
    fields: {
      giftCards: {
        fields: {
          id: string;
          title: string;
          cover: Asset;
          coverStatic?: Asset;
        };
      }[];
    };
  }[];
  isSignedIn: boolean;
  onSignedOutCardPress: () => void;
};
